// English source strings for module 09: contractors, evidence, permits, access periods and
// deliveries (ADR-013, contractors stream). Arabic: ar-contractors.ts.
export const enContractors = {
  // ------------------------------------------------------------ API messages
  'ctr.api.affiliationOverlap': 'This person already works for a company in that period. End the current affiliation first.',
  'ctr.api.alreadySuspended': 'This record is already suspended.',
  'ctr.api.notSuspended': 'This record is not suspended.',
  'ctr.api.deliveryArrived': 'This delivery has already arrived or was cancelled.',
  'ctr.api.deliveryClosed': 'The package outcome is already recorded for this delivery.',
  'ctr.api.deliveryNotArrived': 'Record the delivery’s arrival before its handover.',
  'ctr.api.evidenceDecided': 'This evidence has already been reviewed. Submit a replacement to change it.',
  'ctr.api.evidenceSuperseded': 'A newer accepted revision replaced this evidence.',
  'ctr.api.fileAlreadyAttached': 'A file is already attached to this revision. Submit a replacement instead.',
  'ctr.api.noHandoverArea': 'No approved handover area is open. Open an area or refuse the delivery — couriers are never sent along the visitor route.',
  'ctr.api.notOnsite': 'No arrival is recorded for this, so there is no departure to record.',
  'ctr.api.ownSubmission': 'You drafted or submitted this, so another reviewer must decide it.',
  'ctr.api.periodEnded': 'This access period has ended.',
  'ctr.api.periodHostInactive': 'The accountable host for this access period is inactive. Reassign it before creating visits.',
  'ctr.api.permitClosed': 'This permit was withdrawn.',
  'ctr.api.permitDraftNotWithdrawable': 'This permit is still a draft and was never submitted, so it cannot be withdrawn. Edit it or leave it unsubmitted.',
  'ctr.api.permitNotEditable': 'Only a draft, rejected or invalidated permit can be submitted.',
  'ctr.api.permitNotReady': 'The permit cannot be approved until the required evidence is accepted and valid for the whole permit window.',
  'ctr.api.permitNotSubmitted': 'This permit is not waiting for a decision.',
  'ctr.api.scanPending': 'The malware scan has not passed, so this file cannot be previewed or accepted.',
  'ctr.api.taskResolved': 'This recovery task is already resolved.',
  'ctr.api.typeRetired': 'This evidence type is not in the current requirements, so it cannot be accepted.',
  'ctr.api.workPackageClosed': 'This work package is closed.',

  // ------------------------------------------------------------ validation
  'ctr.v.affiliationUnknown': 'Choose a current worker affiliation.',
  'ctr.v.backupSame': 'The backup sponsor must be a different person.',
  'ctr.v.categoryNotContractor': 'Choose an active visitor category governed by contractor requirements.',
  'ctr.v.companyNotActive': 'This company is suspended or inactive.',
  'ctr.v.companyUnknown': 'Choose a registered company.',
  'ctr.v.confirmImpact': 'Review the impact and confirm.',
  'ctr.v.deliveryPointExists': 'This location is already a handover area.',
  'ctr.v.deliveryPointUnknown': 'Choose an approved handover area at this site.',
  'ctr.v.emptyRule': 'A rule must require at least one evidence type.',
  'ctr.v.evidenceType': 'Upload a PDF, PNG or JPEG file.',
  'ctr.v.expiryRequired': 'This evidence type needs an expiry date before it can be accepted.',
  'ctr.v.handedToRequired': 'Say who received the package.',
  'ctr.v.issuerRequired': 'This evidence type needs an issuer before it can be accepted.',
  'ctr.v.moveBeforeStart': 'The new affiliation must start after the current one began.',
  'ctr.v.permitTooLong': 'Permits of this type can last at most {days} days.',
  'ctr.v.permitTypeUnknown': 'Choose an active permit type.',
  'ctr.v.plate': 'Use letters, digits, spaces or hyphens (up to 20).',
  'ctr.v.reasonRequired': 'Give a reason.',
  'ctr.v.referenceTaken': 'This reference is already used at this site.',
  'ctr.v.registrationTaken': 'Another company has this registration reference.',
  'ctr.v.replacesMismatch': 'A replacement must be the same evidence type for the same subject and site.',
  'ctr.v.sameCompany': 'Choose a different company.',
  'ctr.v.unknownType': 'Refers to an evidence type that does not exist here.',
  'ctr.v.userUnavailable': 'Choose an active colleague.',
  'ctr.v.workPackageClosed': 'This work package is closed.',
  'ctr.v.workPackageUnknown': 'Choose a work package of this company at this site.',
  'ctr.v.workerUnknown': 'This person is not a registered contractor worker.',
  'ctr.v.workersInvalid': 'Choose current workers of the selected company.',
  'ctr.v.unknown_type': 'Choose a configured evidence type.',
  'ctr.v.subject_mismatch': 'This evidence type belongs to a different subject (company, worker or affiliation).',
  'ctr.v.site_required': 'This evidence is site-specific: choose the site it was given for.',
  'ctr.v.site_not_allowed': 'This evidence type applies at every site; do not choose a site.',
  'ctr.v.validity_order': 'The expiry must be on or after the start date.',
  'ctr.v.validity_too_long': 'This validity is longer than the type allows.',
  'ctr.v.period.work_package_closed': 'The work package is closed.',
  'ctr.v.period.outside_work_package': 'The period must lie inside the work package dates.',
  'ctr.v.period.order': 'The end must be on or after the start.',
  'ctr.v.period.daily_order': 'The daily end must be after the daily start.',
  'ctr.v.period.too_long': 'A period can last at most one year.',

  // ------------------------------------------------------------ eligibility and readiness
  'ctr.block.not_affiliated': 'Not a current worker of a registered company for the whole window.',
  'ctr.block.company_suspended': 'The company is suspended.',
  'ctr.block.company_inactive': 'The company is inactive.',
  'ctr.block.person_suspended': 'This worker is suspended.',
  'ctr.block.sponsor_reassignment_required': 'The company has no active accountable sponsor. Reassign the sponsor.',
  'ctr.block.evidence': '{type}: {status}',
  'ctr.block.permit_required': 'A restricted area needs an approved permit covering this worker, zone and the whole window.',
  'ctr.block.permit_evidence': 'Permit evidence — {type}: {status}',
  'ctr.block.work_package_closed': 'The work package is closed.',
  'ctr.block.outside_work_package': 'The visit is outside the work package dates.',
  'ctr.block.access_period_ended': 'The access period has ended.',
  'ctr.block.outside_access_period': 'The visit is outside the approved access period.',
  'ctr.req.satisfied': 'Accepted and valid',
  'ctr.req.missing': 'Missing',
  'ctr.req.awaiting_file': 'Awaiting upload',
  'ctr.req.scan_pending': 'Malware scan pending',
  'ctr.req.scan_failed': 'Scan unavailable',
  'ctr.req.review_pending': 'Review pending',
  'ctr.req.rejected': 'Rejected',
  'ctr.req.expires_in_window': 'Expires before the window ends',
  'ctr.req.not_yet_valid': 'Not yet valid',
  'ctr.permit.p.company_not_active': 'The company is suspended or inactive.',
  'ctr.permit.p.type_inactive': 'This permit type is not active.',
  'ctr.permit.p.too_long': 'The window is longer than this permit type allows.',
  'ctr.permit.p.no_workers': 'Name at least one worker.',
  'ctr.permit.p.worker_suspended': '{worker} is suspended.',
  'ctr.permit.p.affiliation_window': '{worker} does not work for this company for the whole permit window.',
  'ctr.permit.p.evidence': '{worker} — {type}: {status}',

  // ------------------------------------------------------------ delivery notice (worker)
  'ctr.mail.deliverySubject': 'Delivery waiting for you — {reference}',
  'ctr.mail.deliveryBody': 'Hello {name},\n\n{courier} has a delivery for you at {site} ({count} package(s), reference {reference}).\nHandover area: {point}. {route}\n\nThis notice does not record a handover: the desk records whether the package is handed over or refused.',
  // ------------------------------------------------------------ web UI
  'ctr.tab.permits': "Work permits",
  'ctr.tab.companies': "Companies & workers",
  'ctr.tab.evidence': "Evidence register",
  'ctr.tab.rules': "Evidence rules",
  'ctr.ui.intro': "Keep company status, work authorisation and individual arrival checks separate.",
  'ctr.ui.newPermit': "+ New work permit",
  'ctr.ui.permitQueue': "Permit review queue",
  'ctr.ui.permitQueueSub': "Approval here does not bypass visitor identity or visit approval.",
  'ctr.ui.view.queue': "Awaiting decision",
  'ctr.ui.view.all': "All",
  'ctr.ui.view.review': "To review",
  'ctr.ui.view.expiring': "Expiry tasks",
  'ctr.ui.noPermits': "No permits in this view.",
  'ctr.col.permit': "Permit",
  'ctr.col.companyZone': "Company / zone",
  'ctr.col.window': "Window",
  'ctr.col.evidence': "Evidence",
  'ctr.col.status': "Status",
  'ctr.col.action': "Action",
  'ctr.col.reference': "Reference",
  'ctr.col.company': "Company",
  'ctr.col.dates': "Dates",
  'ctr.col.owner': "Owner",
  'ctr.col.workers': "Workers",
  'ctr.col.companyEvidence': "Company evidence",
  'ctr.col.subject': "Subject",
  'ctr.col.state': "State",
  'ctr.col.validity': "Validity",
  'ctr.col.review': "Review",
  'ctr.col.expires': "Expires",
  'ctr.col.replacement': "Replacement",
  'ctr.col.task': "Task",
  'ctr.col.visit': "Visit",
  'ctr.col.observed': "Current facts",
  'ctr.col.type': "Type",
  'ctr.col.siteSpecific': "Per site",
  'ctr.col.expiryRequired': "Expiry required",
  'ctr.col.leadDays': "Alert lead (days)",
  'ctr.col.requires': "Requires",
  'ctr.col.maxDays': "Max days",
  'ctr.col.zoneDependent': "Zone-dependent",
  'ctr.col.active': "Active",
  'ctr.ui.evidenceReady': "Evidence ready",
  'ctr.ui.evidenceNotReady': "Evidence not ready",
  'ctr.ui.reviewPermit': "Review permit",
  'ctr.ui.workPackages': "Work packages",
  'ctr.ui.workPackagesSub': "Contract, PO/project, scope, dates and owner. Commercial details are visible only to contractor managers.",
  'ctr.ui.newWorkPackage': "+ New work package",
  'ctr.ui.newWorkPackageTitle': "New work package",
  'ctr.ui.noWorkPackages': "No work packages at this site yet.",
  'ctr.ui.openWorkPackage': "Open",
  'ctr.ui.inactive': "Inactive",
  'ctr.ui.openEnded': "open-ended",
  'ctr.ui.draftPermit': "Draft work permit",
  'ctr.ui.draftPermitSub': "Work authorisation does not replace individual visitor checks.",
  'ctr.ui.createPermit': "Create permit",
  'ctr.ui.chooseCompanyFirst': "Choose a company to list its current workers.",
  'ctr.f.permitType': "Permit type",
  'ctr.f.company': "Company",
  'ctr.f.workScope': "Work scope",
  'ctr.f.zone': "Zone",
  'ctr.f.workPackage': "Work package",
  'ctr.f.validFrom': "Valid from",
  'ctr.f.validUntil': "Valid until",
  'ctr.f.validFromTime': "Start time",
  'ctr.f.validUntilTime': "End time",
  'ctr.f.issuer': "Issuer",
  'ctr.f.workers': "Workers",
  'ctr.f.worker': "Worker",
  'ctr.f.permitDecision': "Permit decision",
  'ctr.f.contractRef': "Contract reference",
  'ctr.f.scope': "Scope",
  'ctr.f.startsOn': "Starts on",
  'ctr.f.endsOn': "Ends on",
  'ctr.f.owner': "Owner",
  'ctr.f.purchaseOrder': "PO / project",
  'ctr.f.commercialNote': "Commercial note",
  'ctr.f.accountableHost': "Accountable host",
  'ctr.f.category': "Visitor category",
  'ctr.f.destination': "Destination",
  'ctr.f.dailyStart': "Daily start",
  'ctr.f.dailyEnd': "Daily end",
  'ctr.f.weekdays': "Weekdays",
  'ctr.f.exclusions': "Excluded dates",
  'ctr.f.kind': "Kind",
  'ctr.f.registrationRef': "Registration (trade licence)",
  'ctr.f.contactName': "Contact",
  'ctr.f.contactEmail': "Email",
  'ctr.f.contactPhone': "Phone",
  'ctr.f.sponsor': "Sponsor",
  'ctr.f.backupSponsor': "Backup sponsor",
  'ctr.f.department': "Department",
  'ctr.f.effectiveFrom': "Effective from",
  'ctr.f.companyState': "Company state",
  'ctr.f.givenName': "Given name",
  'ctr.f.familyName': "Family name",
  'ctr.f.mobile': "Mobile",
  'ctr.f.role': "Role",
  'ctr.f.resolution': "What was done",
  'ctr.f.evidenceType': "Evidence type",
  'ctr.f.site': "Site",
  'ctr.f.source': "Source",
  'ctr.f.documentRef': "Document number",
  'ctr.f.validFromDate': "Valid from",
  'ctr.f.validUntilDate': "Expires on",
  'ctr.f.evidenceOutcome': "Evidence outcome",
  'ctr.f.versionNote': "Version note",
  'ctr.ui.reviewWorkPermit': "Review work permit",
  'ctr.ui.permitBoundary': "A permit does not replace individual visitor checks and never authorises entry on its own.",
  'ctr.ui.evidenceForPermit': "Evidence for this permit",
  'ctr.ui.decisions': "Decisions",
  'ctr.ui.revision': "revision {n}",
  'ctr.ui.ownSubmission': "You drafted or submitted this permit, so another reviewer must decide it.",
  'ctr.ui.approveNeedsEvidence': "Approval is refused until every named worker’s evidence is accepted and valid for the whole permit window.",
  'ctr.ui.reasonHintReject': "Required when rejecting.",
  'ctr.ui.recordDecision': "Record permit decision",
  'ctr.ui.permitAdmin': "Permit administration",
  'ctr.ui.submitPermit': "Submit for review",
  'ctr.ui.moveZone': "Move to zone…",
  'ctr.ui.changeZone': "Change zone",
  'ctr.ui.withdrawPermit': "Withdraw",
  'ctr.ui.reasonFirst': "Give a reason first.",
  'ctr.ui.zoneChangeWarning': "Changing the zone of an approved permit invalidates its decision; it must be reviewed again.",
  'ctr.ui.commercialHint': "Shown only to contractor managers at this site.",
  'ctr.ui.createWorkPackage': "Create work package",
  'ctr.ui.accessPeriods': "Recurring access periods",
  'ctr.ui.accessPeriodsSub': "Visits are created as drafts within the contract, period and weekday bounds; the host still submits them for approval.",
  'ctr.ui.materialised': "{created} draft visits created; {skipped} dates already had one.",
  'ctr.ui.noPeriods': "No access periods yet.",
  'ctr.ui.periodCounts': "{remaining} dates remaining · {done} visits created",
  'ctr.ui.materialise': "Create draft visits",
  'ctr.ui.newPeriod': "+ New access period",
  'ctr.ui.contractChanges': "Contract changes",
  'ctr.ui.renewHint': "Extending the contract does not extend existing access periods — create a new period.",
  'ctr.ui.saveDates': "Save dates",
  'ctr.ui.closeHint': "Closing stops new occurrences and ends its access periods. Past attendance is kept.",
  'ctr.ui.closeWorkPackage': "Close work package",
  'ctr.ui.exclusionsHint': "Dates as YYYY-MM-DD, separated by commas or spaces.",
  'ctr.ui.createPeriod': "Create access period",
  'ctr.weekday.1': "Mon",
  'ctr.weekday.2': "Tue",
  'ctr.weekday.3': "Wed",
  'ctr.weekday.4': "Thu",
  'ctr.weekday.5': "Fri",
  'ctr.weekday.6': "Sat",
  'ctr.weekday.7': "Sun",
  'ctr.ui.reassignmentNeeded': { one: "1 company has no active accountable sponsor. Reassign before new work.", other: "{count} companies have no active accountable sponsor. Reassign before new work." },
  'ctr.ui.companies': "Contractor companies",
  'ctr.ui.companiesSub': "Companies are kept separate from individual workers and access rights.",
  'ctr.ui.registerCompany': "Register company",
  'ctr.ui.registerCompanyTitle': "Register contractor company",
  'ctr.ui.registerCompanySub': "A company record is not an access right. Every company needs an accountable sponsor.",
  'ctr.ui.noCompanies': "No contractor companies yet.",
  'ctr.ui.reassign': "Sponsor reassignment needed",
  'ctr.ui.chooseCompany': "Choose a company to see its sponsor, workers and evidence.",
  'ctr.ui.sponsorHint': "An active employee accountable for this company.",
  'ctr.ui.companySub': "Company facts, kept separate from access.",
  'ctr.ui.backup': "backup: {name}",
  'ctr.ui.sponsorGone': "The sponsor (and any backup) is no longer active. Future work is held until a sponsor is reassigned.",
  'ctr.ui.addWorker': "Add worker",
  'ctr.ui.reassignSponsor': "Reassign sponsor",
  'ctr.ui.reviewSuspension': "Review suspension impact",
  'ctr.ui.reinstate': "Reinstate",
  'ctr.ui.reinstateSub': "Reinstating does not resolve recovery tasks or restore invalidated permits.",
  'ctr.ui.workers': "Current workers",
  'ctr.ui.noWorkers': "No current workers.",
  'ctr.ui.since': "since",
  'ctr.ui.workerProfile': "Profile",
  'ctr.ui.formerWorkers': "Former workers",
  'ctr.ui.sponsorHistory': "Sponsor history",
  'ctr.ui.suspensions': "Suspensions",
  'ctr.ui.lifted': "lifted",
  'ctr.ui.mobileHint': "Contractor visits may require a mobile number.",
  'ctr.ui.suspendTitle': "Suspend {name}",
  'ctr.ui.suspendSub': "Impact: future contractor check-ins are blocked; existing presence is not rewritten.",
  'ctr.ui.suspendWarning': "Any active logical access requires a separate recovery task. This does not imply departure or successful revocation.",
  'ctr.ui.confirmImpact': "I have reviewed the impact. Recovery tasks will be opened for each affected visit.",
  'ctr.ui.suspend': "Suspend",
  'ctr.ui.suspendNeedsAuthority': "Suspending needs the contractor suspension authority (a specialist role assigned by an organisation administrator).",
  'ctr.impact.futureVisits': "Future or active visits",
  'ctr.impact.observedOnsite': "Observed onsite now",
  'ctr.impact.activeCredentials': "Live or unknown access",
  'ctr.impact.permits': "Permits to invalidate",
  'ctr.ui.workerSub': "Person, company, role, evidence and visit history.",
  'ctr.ui.eligibilityCheck': "Check interval and evidence",
  'ctr.ui.eligibilityCheckSub': "Evaluated against current evidence across the whole window, at this site.",
  'ctr.ui.checkEligibility': "Check",
  'ctr.ui.noEvidence': "No evidence recorded.",
  'ctr.ui.superseded': "Superseded",
  'ctr.ui.history': "Employment and visit history",
  'ctr.ui.partialScope': "Visits at sites outside your scope are not shown.",
  'ctr.ui.moveWorker': "Move to another company",
  'ctr.ui.moveWorkerSub': "A new affiliation starts; permits, affiliation evidence and access periods of the old company do not carry over.",
  'ctr.ui.recovery': "Suspension recovery work",
  'ctr.ui.recoverySub': "Open follow-ups created by suspensions. Presence and access facts are shown as they are now.",
  'ctr.ui.resolve': "Resolve",
  'ctr.ui.resolveSub': "Record what was done. Resolving does not change presence or access facts.",
  'ctr.task.review_visit': "Review future visit",
  'ctr.task.revoke_access': "Request access revocation",
  'ctr.task.occupant_onsite': "Person still onsite",
  'ctr.ui.noScanner': "No malware scanner is configured yet: uploaded files stay unavailable for preview and acceptance. Reviewers may accept an original document they have seen in person.",
  'ctr.ui.evidenceTitle': "Evidence and induction",
  'ctr.ui.evidenceSub': "A received document is not an accepted document.",
  'ctr.ui.addEvidence': "+ Add evidence",
  'ctr.ui.noExpiry': "No expiry alerts.",
  'ctr.ui.alertDue': "alert {date} ({days} days before)",
  'ctr.ui.reviewEvidence': "Review evidence",
  'ctr.ui.inspect': "Inspect",
  'ctr.ui.reviewEvidenceTitle': "Review evidence",
  'ctr.ui.scan': "Scan",
  'ctr.ui.review': "Review",
  'ctr.ui.validity': "Validity",
  'ctr.ui.openFile': "Open document",
  'ctr.ui.previewBlocked': "The malware scan has not passed, so this file cannot be previewed or accepted.",
  'ctr.ui.originalSighted': "Recorded from an original document seen in person; no file is stored.",
  'ctr.ui.reviewBlockedScan': "Review is blocked until a file has been received and its malware scan has passed.",
  'ctr.ui.reviewNotYours': "Another reviewer must decide this: you submitted it, or it is outside your review scope.",
  'ctr.ui.accept': "Accept",
  'ctr.ui.reject': "Reject",
  'ctr.ui.recordReview': "Record review",
  'ctr.ui.replace': "Submit replacement",
  'ctr.ui.replaceTitle': "Submit replacement evidence",
  'ctr.ui.uploadFile': "Attach file",
  'ctr.ui.uploadHint': "PDF, PNG or JPEG up to 5 MB. Checked by content; then held for a malware scan.",
  'ctr.ui.upload': "Upload",
  'ctr.ui.siteSpecificHint': "Inductions count only at the site they were given for.",
  'ctr.ui.uploadThenScan': "You can attach the file next. It stays unavailable until a malware scan passes.",
  'ctr.ui.sightedHint': "You have seen the original document. Another reviewer still has to accept it.",
  'ctr.ui.submitEvidence': "Submit for review",
  'ctr.source.upload': "Uploaded file",
  'ctr.source.original_sighted': "Original sighted",
  'ctr.subject.company': "Company",
  'ctr.subject.affiliation': "Worker at this company",
  'ctr.subject.person': "Person",
  'ctr.ui.rulesNote': "Version {n} is in force. Changes are drafted and published as a new version; evidence and permits keep the version they were made under.",
  'ctr.ui.evidenceTypes': "Evidence types",
  'ctr.ui.evidenceTypesSub': "Who the evidence is about, whether it is per site, and when alerts start.",
  'ctr.ui.rules': "Requirements by visitor category",
  'ctr.ui.rulesSub': "Evidence required for each contractor category, checked across the whole visit window.",
  'ctr.ui.restrictedRequiresPermit': "Restricted destinations also need an approved permit covering the visit",
  'ctr.ui.requireSponsor': "Companies need an active accountable sponsor",
  'ctr.ui.permitTypes': "Permit types",
  'ctr.ui.permitTypesSub': "Evidence each named worker needs, maximum length, and whether a zone change invalidates approval.",
  'ctr.ui.saveDraft': "Save draft",
  'ctr.ui.publish': "Publish version {n}",
  'ctr.ui.discardDraft': "Discard draft",
  'ctr.ui.unsaved': "You have unsaved changes.",
  'ctr.ui.versionHistory': "Version history",
  'ctr.ui.versionN': "Version {n}",
  'ctr.kind.contractor': "Contractor",
  'ctr.kind.vendor': "Vendor",
  'ctr.kindOf.insurance': "Insurance",
  'ctr.kindOf.licence': "Licence",
  'ctr.kindOf.noc': "NOC",
  'ctr.kindOf.certification': "Certification",
  'ctr.kindOf.induction': "Induction",
  'ctr.kindOf.training': "Training",
  'ctr.kindOf.other': "Other",
  'ctr.invalidated.zone_changed': "Work area changed after approval",
  'ctr.invalidated.window_changed': "Window changed after approval",
  'ctr.invalidated.scope_changed': "Scope changed after approval",
  'ctr.invalidated.workers_changed': "Named workers changed after approval",
  'ctr.invalidated.company_suspended': "Company suspended",
  'ctr.invalidated.work_package_closed': "Work package closed",
  'ctr.ui.noRequirements': "No requirements apply.",
  'ctr.ui.validUntil': "valid until",
  'ctr.ui.notGoverned': "This visitor category has no contractor requirements.",
  'ctr.ui.eligible': "Contractor checks met",
  'ctr.ui.notEligible': "Contractor checks not met",
  'ctr.ui.permitCovers': "Approved permit covers this",
  'ctr.ui.permitMissing': "Permit required",
  'ctr.ui.requirementSet': "Requirements version {n}",
  'ctr.ui.readinessTitle': "Contractor readiness",
  'ctr.ui.readinessSub': "Company, personal and site evidence for the whole visit window. Visit approval and presence are separate facts.",
  'ctr.status.permit.draft': "Draft",
  'ctr.status.permit.submitted': "Review required",
  'ctr.status.permit.approved': "Approved",
  'ctr.status.permit.rejected': "Rejected",
  'ctr.status.permit.invalidated': "Invalidated",
  'ctr.status.permit.withdrawn': "Withdrawn",
  'ctr.status.company.active': "Active",
  'ctr.status.company.suspended': "Suspended",
  'ctr.status.company.inactive': "Inactive",
  'ctr.status.workPackage.active': "Active",
  'ctr.status.workPackage.closed': "Closed",
  'ctr.status.period.active': "Active",
  'ctr.status.period.ended': "Ended",
  'ctr.status.alert.scheduled': "Scheduled",
  'ctr.status.alert.raised': "Raised",
  'ctr.status.alert.cancelled': "Cancelled (replaced)",
  'ctr.status.review.pending': "Review pending",
  'ctr.status.review.accepted': "Accepted",
  'ctr.status.review.rejected': "Rejected",
  'ctr.status.scan.no_file': "No file yet",
  'ctr.status.scan.pending': "Scan pending",
  'ctr.status.scan.clean': "Scan passed",
  'ctr.status.scan.infected': "Rejected by malware check", // PD-707: neutral wording (clamav stream)
  'ctr.status.scan.failed': "Malware check not completed",
  'ctr.status.scan.not_applicable': "No file (original sighted)",
  'ctr.status.package.pending': "Waiting",
  'ctr.status.package.handed_over': "Handed over",
  'ctr.status.package.refused': "Refused",
  'ctr.status.package.cancelled': "Cancelled",
  'ctr.status.notice.queued': "Queued",
  'ctr.status.notice.accepted': "Accepted by mail service",
  'ctr.status.notice.failed': "Failed",
  'ctr.status.notice.blocked': "Not sent",
  'ctr.status.area.open': "Open",
  'ctr.status.area.closed': "Closed",
  'ctr.status.version.draft': "Draft",
  'ctr.status.version.published': "Published",
  'ctr.status.version.superseded': "Superseded",
  'ctr.status.version.discarded': "Discarded",
  'dlv.intro': "Track recipient contact and physical handover as different outcomes.",
  'dlv.register': "+ Register delivery",
  'dlv.expect': "Expect a delivery",
  'dlv.m.open': "Waiting packages",
  'dlv.m.openSub': "Arrived, outcome not recorded",
  'dlv.m.awaitingDeparture': "Awaiting departure",
  'dlv.m.awaitingDepartureSub': "Package closed; courier or vehicle still here",
  'dlv.m.unknownRecipient': "Unknown recipient",
  'dlv.m.unknownRecipientSub': "Held with an escalation owner",
  'dlv.m.expectedToday': "Expected today",
  'dlv.m.expectedTodaySub': "Announced, not yet arrived",
  'dlv.tab.open': "Delivery desk",
  'dlv.tab.expected': "Expected",
  'dlv.tab.closed': "Closed",
  'dlv.tab.areas': "Handover areas",
  'dlv.tab.mine': "My deliveries",
  'dlv.mine': "Deliveries for you",
  'dlv.mineSub': "A notice tells you a delivery is waiting; the desk records the handover.",
  'dlv.desk': "Delivery desk",
  'dlv.deskSub': "Recipient notices are sent through the mail service; a notice is never a handover.",
  'dlv.empty': "No deliveries in this view.",
  'dlv.col.reference': "Reference",
  'dlv.col.courier': "Courier / recipient",
  'dlv.col.package': "Package",
  'dlv.col.handover': "Handover",
  'dlv.col.people': "Courier / vehicle",
  'dlv.col.action': "Action",
  'dlv.separateNote': "Courier departure and vehicle exit must be observed separately. A handed-over package does not prove either movement.",
  'dlv.expectedOn': "expected",
  'dlv.for': "For {name}",
  'dlv.unknownRecipient': "Recipient unknown ({stated})",
  'dlv.packages': { one: "1 package", other: "{count} packages" },
  'dlv.notice': "Notice",
  'dlv.vehicle': "Vehicle",
  'dlv.courier': "Courier",
  'dlv.recordHandover': "Record handover",
  'dlv.expectTitle': "Expect a delivery",
  'dlv.expectSub': "The desk records its arrival; nothing is sent to the courier.",
  'dlv.registerTitle': "Register delivery",
  'dlv.registerSub': "The courier is directed to an approved handover area, never the visitor route.",
  'dlv.f.courier': "Courier",
  'dlv.f.driver': "Driver",
  'dlv.f.vehicle': "Vehicle plate",
  'dlv.f.packageRef': "Package reference",
  'dlv.f.count': "Packages",
  'dlv.f.expectedOn': "Expected on",
  'dlv.f.recipient': "Recipient",
  'dlv.f.stated': "Recipient as stated",
  'dlv.f.area': "Handover area",
  'dlv.f.note': "Note",
  'dlv.f.owner': "Escalation owner",
  'dlv.f.handedTo': "Handed to",
  'dlv.f.handedToName': "Name of the person who received it",
  'dlv.f.refusal': "Refusal reason",
  'dlv.f.location': "Location",
  'dlv.f.priority': "Priority",
  'dlv.f.route': "Route",
  'dlv.f.contact': "Contact",
  'dlv.recipientHint': "Leave unknown if nobody matches; you become the escalation owner.",
  'dlv.recipientUnknown': "Unknown — hold for escalation",
  'dlv.statedHint': "What the courier said, to help identify the recipient later.",
  'dlv.areaHint': "Leave automatic to use the highest-priority open area.",
  'dlv.areaAuto': "Automatic",
  'dlv.selfRecipient': "You are the recipient of a delivery you announce.",
  'dlv.expectSave': "Save expected delivery",
  'dlv.registerSave': "Register delivery",
  'dlv.recordTitle': "Record package handover",
  'dlv.recordSub': "Person and vehicle departure are recorded separately.",
  'dlv.facts': "Separate facts",
  'dlv.noticeNote': "An accepted notice means the mail service took the message — not that the recipient read it or accepted the package.",
  'dlv.arrive': "Record arrival",
  'dlv.arriveSave': "Courier arrived",
  'dlv.someoneElse': "Someone else (enter a name)",
  'dlv.departures': "Departures",
  'dlv.courierLeft': "Courier left",
  'dlv.vehicleLeft': "Vehicle left",
  'dlv.recipientContact': "Recipient contact",
  'dlv.assign': "Assign recipient",
  'dlv.resend': "Send notice again",
  'dlv.escalate': "Escalate",
  'dlv.cancel': "Cancel expected delivery",
  'dlv.notices': "Notices",
  'dlv.trail': "Custody trail",
  'dlv.system': "system",
  'dlv.reason.no_email': "recipient has no email address",
  'dlv.reason.recipient_inactive': "recipient is inactive",
  'dlv.event.registered': "Registered",
  'dlv.event.expected': "Announced",
  'dlv.event.arrived': "Arrived",
  'dlv.event.recipient_assigned': "Recipient assigned",
  'dlv.event.escalated': "Escalated",
  'dlv.event.notice_queued': "Notice queued",
  'dlv.event.notice_accepted': "Notice accepted by mail service",
  'dlv.event.notice_failed': "Notice not sent",
  'dlv.event.handed_over': "Handed over",
  'dlv.event.refused': "Refused",
  'dlv.event.courier_departed': "Courier departed",
  'dlv.event.vehicle_departed': "Vehicle departed",
  'dlv.event.cancelled': "Cancelled",
  'dlv.areas': "Approved handover areas",
  'dlv.areasSub': "Couriers are directed here instead of the visitor route. A closed area is skipped; with none open, deliveries are refused.",
  'dlv.noAreas': "No handover area is configured. Deliveries cannot be registered until a site administrator adds one.",
  'dlv.priority': "priority {n}",
  'dlv.capacity': "capacity about {n}",
  'dlv.waiting': "{n} waiting here",
  'dlv.closeArea': "Close",
  'dlv.openArea': "Open",
  'dlv.priorityHint': "Lower numbers are used first.",
  'dlv.view': 'View',
  'dlv.addArea': "Add handover area",
} as const;
